import { UnitStateV2 } from '@eppendorf/vnls-device-service-utils';
import { Badge, HasClassName } from '@eppendorf/vnls-react-components';
import { ReactElement } from 'react';

import { getLocalizedDate } from '$shared/utils/date.utils';

import { getActivityStatusTextSnapshot } from '$features/monitoring/activity-status/activity-status.utils-snapshot';

export interface ActivityStatusSnapshotProps extends HasClassName {
  activityStatus?: UnitStateV2 | null;
  bordered?: boolean;
  offlineSince?: string;
}

function getStatusColor(
  status: UnitStateV2,
): 'green' | 'orange' | 'blue' | 'red' | undefined {
  switch (status) {
    case UnitStateV2.Paused:
    case UnitStateV2.Hold:
    case UnitStateV2.Preparing:
      return 'orange';
    case UnitStateV2.AutomaticallyRunning:
    case UnitStateV2.ManuallyRunning:
    case UnitStateV2.Running:
    case UnitStateV2.Done:
    case UnitStateV2.Online:
      return 'green';
    case UnitStateV2.Idle:
      return 'blue';
    case UnitStateV2.Unreachable:
    case UnitStateV2.Error:
      return 'red';
    default:
      return undefined;
  }
}

export function ActivityStatusSnapshot({
  activityStatus,
  className,
  bordered = false,
  offlineSince,
}: ActivityStatusSnapshotProps): ReactElement | null {
  if (!activityStatus) return null;

  if (activityStatus === UnitStateV2.Offline) {
    const text = getActivityStatusTextSnapshot(UnitStateV2.Offline);
    const since = offlineSince ? ` since ${getLocalizedDate(offlineSince)}` : '';
    return <Badge bordered={bordered}>{text + since}</Badge>;
  }

  const color = getStatusColor(
    activityStatus as Exclude<UnitStateV2, UnitStateV2.Offline>,
  );
  const text = getActivityStatusTextSnapshot(activityStatus);

  const badge = (
    <Badge bordered={bordered} color={color}>
      {text}
    </Badge>
  );

  return <div className={className}>{badge}</div>;
}
